import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import SEO, { seoConfigs } from '../../components/SEO';
import { concepts } from '../data/concepts';
import { ChevronRight, LayoutTemplate, ArrowRight } from 'lucide-react';
import Diagram from '../components/Diagram';

const CoreConcepts = () => {
    const location = useLocation();

    // Initialize active state from URL param or default to first
    const getInitialId = () => {
        const params = new URLSearchParams(location.search);
        const urlId = params.get('id');
        if (urlId && concepts.some(c => c.id === urlId)) {
            return urlId;
        }
        return concepts[0].id;
    };

    const [activeId, setActiveId] = useState(getInitialId());

    // Update active state if URL param changes
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const urlId = params.get('id');
        if (urlId && concepts.some(c => c.id === urlId)) {
            setActiveId(urlId);
        }
    }, [location.search]);

    const activeConcept = concepts.find(c => c.id === activeId) || concepts[0];
    const activeIndex = concepts.findIndex(c => c.id === activeId);
    const nextConcept = activeIndex < concepts.length - 1 ? concepts[activeIndex + 1] : null;

    // Group concepts by category
    const groupedConcepts = concepts.reduce((acc, curr) => {
        if (!acc[curr.category]) acc[curr.category] = [];
        acc[curr.category].push(curr);
        return acc;
    }, {});

    // Scroll to top when switching topics
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [activeId]);

    const handleNextTopic = () => {
        if (nextConcept) setActiveId(nextConcept.id);
    };

    return (
        <>
            <SEO
                title={`${activeConcept.title} — Core System Design Concepts | Rastor AI`}
                description={`Deep dive into ${activeConcept.title}. ${seoConfigs.sysdesignConcepts.description}`}
                keywords={`${activeConcept.title}, ${seoConfigs.sysdesignConcepts.keywords}`}
                canonicalUrl={`/sysdesign/concepts?id=${activeConcept.id}`}
            />

            <div className="sd-container sd-fade-in">
                <div className="sd-doc-header sd-mb-4">
                    <div className="sd-badge sd-mb-2"><LayoutTemplate size={14} className="sd-inline-icon" /> Fundamentals</div>
                    <h1>Core <span className="sd-text-gradient">Concepts</span></h1>
                    <p>The foundational building blocks you need to understand before architecting any large-scale system.</p>
                </div>

                <div className="sd-doc-layout">
                    {/* Sidebar Navigation */}
                    <aside className="sd-doc-sidebar">
                        <nav className="sd-doc-nav">
                            {Object.entries(groupedConcepts).map(([category, items]) => (
                                <div key={category} className="sd-doc-nav-group">
                                    <h4 className="sd-doc-nav-title">
                                        {category}
                                        <span className="sd-topic-count">{items.length}</span>
                                    </h4>
                                    {items.map((concept) => (
                                        <button
                                            key={concept.id}
                                            className={`sd-doc-nav-link ${activeId === concept.id ? 'active' : ''}`}
                                            onClick={() => setActiveId(concept.id)}
                                        >
                                            {concept.title}
                                            {activeId === concept.id && <ChevronRight size={16} className="sd-active-icon" />}
                                        </button>
                                    ))}
                                </div>
                            ))}
                        </nav>
                    </aside>

                    {/* Content Area */}
                    <article className="sd-doc-content">
                        {/* Mobile Navigation Dropdown */}
                        <div className="sd-mobile-nav">
                            <div className="sd-mobile-nav-wrapper">
                                <select
                                    className="sd-mobile-nav-select"
                                    value={activeId}
                                    onChange={(e) => setActiveId(e.target.value)}
                                >
                                    {Object.entries(groupedConcepts).map(([category, items]) => (
                                        <optgroup key={category} label={category}>
                                            {items.map(concept => (
                                                <option key={concept.id} value={concept.id}>
                                                    {concept.title}
                                                </option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="sd-prose">
                            <ReactMarkdown
                                components={{
                                    code({ node, inline, className, children, ...props }) {
                                        const match = /language-(\w+)/.exec(className || '');
                                        if (!inline && match && match[1] === 'mermaid') {
                                            return <Diagram chart={String(children).replace(/\n$/, '')} />;
                                        }
                                        return <code className={className} {...props}>{children}</code>;
                                    }
                                }}
                            >
                                {activeConcept.content.replace(/<diagram>([\s\S]*?)<\/diagram>/g, (match, p1) => `\n\`\`\`mermaid\n${p1.trim()}\n\`\`\`\n`)}
                            </ReactMarkdown>
                        </div>

                        {/* Next Topic */}
                        {nextConcept && (
                            <div className="sd-next-topic" onClick={handleNextTopic}>
                                <div>
                                    <div className="sd-next-topic-label">Next Topic</div>
                                    <div className="sd-next-topic-title">{nextConcept.title} <ArrowRight size={16} style={{ display: 'inline', verticalAlign: 'text-bottom' }} /></div>
                                </div>
                            </div>
                        )}
                    </article>
                </div>
            </div>
        </>
    );
};

export default CoreConcepts;
